import React, { useEffect, useState } from "react";

const ProductDashboard = ({ onLogout }) => {
  const API_BASE =
    import.meta.env.VITE_API_URL ||
    (import.meta.env.DEV
      ? "http://localhost:5000"
      : "https://km-lkmy.onrender.com");

  const BRAND_LIST = [
    "Maruti Suzuki",
    "Hyundai",
    "Mahindra",
    "Tata",
    "Honda",
    "Renault",
    "Toyota",
    "MG Motors",
    "Kia",
    "Volkswagen",
    "Skoda",
    "Others",
  ];

  const CATEGORY_LIST = [
    "Transmission",
    "Trim",
    "Wire",
    "Engine",
    "Body",
    "Chassis",
    "Electric",
  ];

  const ITEMS_PER_PAGE = 20;

  // States
  const [products, setProducts] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterBrand, setFilterBrand] = useState("");

  const [form, setForm] = useState({
    part_name: "",
    part_number: "",
    description: "",
    category: "",
    brand: "",
    image: "",
  });

  const [editingId, setEditingId] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);

  // CSV upload
  const [csvFile, setCsvFile] = useState(null);
  const [csvProgress, setCsvProgress] = useState(0);

  // ZIP upload
  const [zipFile, setZipFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // ------------------------------
  // FETCH PRODUCTS (SERVER PAGINATION)
  // ------------------------------
  const fetchProducts = async (page = 1) => {
    try {
      setLoading(true);
      const res = await fetch(
        `${API_BASE}/api/products?page=${page}&limit=${ITEMS_PER_PAGE}&brand=${filterBrand}&search=${searchTerm}`
      );
      const data = await res.json();

      setProducts(data.products || []);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error("❌ Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(currentPage);
  }, [currentPage, filterBrand, searchTerm]);

  // ------------------------------
  // ADD / EDIT PRODUCT
  // ------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (key !== "image") formData.append(key, value);
      });

      // Only send image if user selected a new file
      if (imageFile) {
        formData.append("image", imageFile);
      }

      const method = editingId ? "PUT" : "POST";
      const url = editingId
        ? `${API_BASE}/api/products/${editingId}`
        : `${API_BASE}/api/products`;

      const res = await fetch(url, { method, body: formData });

      if (!res.ok) throw new Error("Failed to save product");

      // Reset form
      setForm({
        part_name: "",
        part_number: "",
        description: "",
        category: "",
        brand: "",
        image: "",
      });
      setImageFile(null);
      setEditingId(null);

      fetchProducts(currentPage);
    } catch (err) {
      console.error(err);
      alert("❌ Error saving product");
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------
  // DELETE PRODUCT
  // ------------------------------
  const handleDelete = async (id) => {
    if (!confirm("Delete this product?")) return;

    await fetch(`${API_BASE}/api/products/${id}`, { method: "DELETE" });
    fetchProducts(currentPage);
  };

  // ------------------------------
  // EDIT PRODUCT
  // ------------------------------
  const handleEdit = (p) => {
    setEditingId(p.id);
    setForm({
      part_name: p.part_name,
      part_number: p.part_number,
      description: p.description,
      category: p.category,
      brand: p.brand,
      image: p.image,
    });
    setImageFile(null);
  };

  // ------------------------------
  // CSV UPLOAD WITH PROGRESS
  // ------------------------------
  const handleCsvUpload = () => {
    if (!csvFile) return alert("Upload a CSV file first");

    const formData = new FormData();
    formData.append("file", csvFile);

    const xhr = new XMLHttpRequest();

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100);
        setCsvProgress(percent);
      }
    };

    xhr.onload = () => {
      const res = JSON.parse(xhr.responseText);
      alert(`Imported ${res.imported} Products`);
      setCsvProgress(0);
      setCsvFile(null);
      fetchProducts(currentPage);
    };

    xhr.onerror = () => {
      alert("CSV Upload Failed");
      setCsvProgress(0);
    };

    xhr.open("POST", `${API_BASE}/api/products/bulk-upload`);
    xhr.send(formData);
  };

  // ------------------------------
  // ZIP BULK IMAGE UPLOAD + PROGRESS
  // ------------------------------
  const handleZipUpload = () => {
    if (!zipFile) return alert("Upload a ZIP file first");

    const formData = new FormData();
    formData.append("zip", zipFile);

    const xhr = new XMLHttpRequest();

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100);
        setUploadProgress(percent);
      }
    };

    xhr.onload = () => {
      const res = JSON.parse(xhr.responseText);
      alert(`Images Updated: ${res.updated}\nSkipped: ${res.skipped}`);
      setUploadProgress(0);
      setZipFile(null);
      fetchProducts();
    };

    xhr.onerror = () => {
      alert("ZIP Upload Failed");
      setUploadProgress(0);
    };

    xhr.open("POST", `${API_BASE}/api/products/bulk-images`);
    xhr.send(formData);
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white p-8">
      {/* Top Bar */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Admin Dashboard</h1>

        <button
          onClick={() => {
            localStorage.removeItem("isAdmin");
            onLogout();
          }}
          className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg"
        >
          Logout
        </button>
      </div>

      {/* CSV BULK UPLOAD */}
      <div className="bg-slate-800 p-6 rounded-2xl shadow-lg mb-8">
        <h2 className="text-xl mb-3 font-semibold">Bulk CSV Upload</h2>

        <input
          type="file"
          accept=".csv"
          onChange={(e) => setCsvFile(e.target.files[0])}
          className="p-3 rounded-md bg-slate-700 border border-slate-600 w-full"
        />

        {csvProgress > 0 && (
          <div className="w-full bg-slate-600 rounded-full h-3 mt-4">
            <div
              className="bg-blue-500 h-3 rounded-full transition-all"
              style={{ width: `${csvProgress}%` }}
            ></div>
          </div>
        )}

        <button
          onClick={handleCsvUpload}
          className="mt-4 bg-green-600 hover:bg-green-700 px-6 py-2 rounded-md font-semibold"
        >
          Upload CSV
        </button>
      </div>

      {/* ZIP BULK IMAGE UPLOAD */}
      <div className="bg-slate-800 p-6 rounded-2xl shadow-lg mb-8">
        <h2 className="text-xl mb-3 font-semibold">Bulk Image Upload (ZIP)</h2>

        <input
          type="file"
          accept=".zip"
          onChange={(e) => setZipFile(e.target.files[0])}
          className="p-3 rounded-md bg-slate-700 border border-slate-600 w-full"
        />

        {uploadProgress > 0 && (
          <div className="w-full bg-slate-600 rounded-full h-3 mt-4">
            <div
              className="bg-green-500 h-3 rounded-full transition-all"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
        )}

        <button
          onClick={handleZipUpload}
          className="mt-4 bg-green-600 hover:bg-green-700 px-6 py-2 rounded-md font-semibold"
        >
          Upload ZIP
        </button>
      </div>

      {/* ADD / EDIT FORM */}
      <form
        onSubmit={handleSubmit}
        className="bg-slate-800 p-6 rounded-2xl shadow-lg grid grid-cols-2 gap-4 mb-10"
      >
        <input
          type="text"
          placeholder="Part Name"
          value={form.part_name}
          onChange={(e) => setForm({ ...form, part_name: e.target.value })}
          className="p-3 bg-slate-700 border border-slate-600 rounded-md"
          required
        />

        <input
          type="text"
          placeholder="Part Number"
          value={form.part_number}
          onChange={(e) => setForm({ ...form, part_number: e.target.value })}
          className="p-3 bg-slate-700 border border-slate-600 rounded-md"
          required
        />

        <textarea
          placeholder="Description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="col-span-2 p-3 bg-slate-700 border border-slate-600 rounded-md"
        />

        <select
          value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
          className="p-3 bg-slate-700 border border-slate-600 rounded-md"
          required
        >
          <option value="">Select Category</option>
          {CATEGORY_LIST.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>

        <select
          value={form.brand}
          onChange={(e) => setForm({ ...form, brand: e.target.value })}
          className="p-3 bg-slate-700 border border-slate-600 rounded-md"
          required
        >
          <option value="">Select Brand</option>
          {BRAND_LIST.map((b) => (
            <option key={b}>{b}</option>
          ))}
        </select>

        {/* Image Upload */}
        <div className="col-span-2">
          <label className="block mb-1 text-sm">Product Image</label>

          <input
            type="file"
            accept="image/*"
            onChange={(e) => setImageFile(e.target.files[0])}
            className="w-full p-2 bg-slate-700 rounded-md border border-slate-600 cursor-pointer"
          />

          {form.image && !imageFile && (
            <img
              src={form.image}
              className="mt-3 w-24 h-24 rounded-md object-cover"
            />
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="col-span-2 bg-indigo-600 hover:bg-indigo-700 py-3 rounded-md font-semibold disabled:opacity-50"
        >
          {loading ? "Saving..." : editingId ? "Update Product" : "Add Product"}
        </button>
      </form>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <input
          type="text"
          placeholder="Search by part name or number..."
          className="p-3 bg-slate-800 border border-slate-700 rounded-md flex-1"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
        />

        <select
          value={filterBrand}
          onChange={(e) => {
            setFilterBrand(e.target.value);
            setCurrentPage(1);
          }}
          className="p-3 bg-slate-800 border border-slate-700 rounded-md"
        >
          <option value="">All Brands</option>
          {BRAND_LIST.map((b) => (
            <option key={b}>{b}</option>
          ))}
        </select>
      </div>

      {/* PRODUCT TABLE */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead className="bg-slate-800">
            <tr>
              <th className="p-3 text-left">Image</th>
              <th className="p-3 text-left">Part Name</th>
              <th className="p-3 text-left">Number</th>
              <th className="p-3 text-left">Category</th>
              <th className="p-3 text-left">Brand</th>
              <th className="p-3 text-left">Actions</th>
            </tr>
          </thead>

          <tbody>
            {products?.map((p) => (
              <tr
                key={p.id}
                className="border-b border-slate-700 hover:bg-slate-800/50"
              >
                <td className="p-3">
                  <img
                    src={p.image}
                    className="w-16 h-16 object-cover rounded-md"
                  />
                </td>
                <td className="p-3">{p.part_name}</td>
                <td className="p-3">{p.part_number}</td>
                <td className="p-3">{p.category}</td>
                <td className="p-3">{p.brand}</td>

                <td className="p-3 flex gap-2">
                  <button
                    onClick={() => handleEdit(p)}
                    className="px-3 py-1 bg-yellow-500 rounded hover:bg-yellow-600"
                  >
                    Edit
                  </button>

                  <button
                    onClick={() => handleDelete(p.id)}
                    className="px-3 py-1 bg-red-600 rounded hover:bg-red-700"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="flex justify-center items-center gap-4 mt-6">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
            className="px-4 py-2 bg-slate-800 rounded disabled:opacity-50"
          >
            Prev
          </button>

          <span>
            Page {currentPage} of {totalPages}
          </span>

          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
            className="px-4 py-2 bg-slate-800 rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductDashboard;
